import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useSupabase, useTenant } from "@how/app-kit";
import { semanticColor, type SemanticColor } from "@how/design-system";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** The five standings, fixed across churches (docs/modules/membership.md). */
export type Standing = "visitor" | "attending" | "in_process" | "member" | "inactive";
export const STANDING_LABEL: Record<Standing, string> = {
  visitor: "Visitor",
  attending: "Attending",
  in_process: "In process",
  member: "Member",
  inactive: "Inactive",
};
export const STANDING_COLOR: Record<Standing, SemanticColor> = {
  visitor: semanticColor("neutral"),
  attending: semanticColor("info"),
  in_process: semanticColor("warning"),
  member: semanticColor("success"),
  inactive: semanticColor("neutral"),
};
export const STANDINGS = Object.keys(STANDING_LABEL) as Standing[];

export interface Pathway {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly isActive: boolean;
  readonly sortOrder: number;
}
export interface PathwayStep {
  readonly id: string;
  readonly pathwayId: string;
  readonly name: string;
  readonly description: string | null;
  readonly sortOrder: number;
}

export function usePathways() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["membership-pathways", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<Pathway[]> => {
      const { data, error } = await supabase
        .from("membership_pathways")
        .select("id, name, description, is_active, sort_order")
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((w) => ({
        id: w.id,
        name: w.name,
        description: w.description,
        isActive: w.is_active,
        sortOrder: w.sort_order,
      }));
    },
  });
}

export function usePathwaySteps() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["membership-steps", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<PathwayStep[]> => {
      const { data, error } = await supabase
        .from("membership_steps")
        .select("id, pathway_id, name, description, sort_order")
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((s) => ({
        id: s.id,
        pathwayId: s.pathway_id,
        name: s.name,
        description: s.description,
        sortOrder: s.sort_order,
      }));
    },
  });
}

export interface MembershipRow {
  readonly personId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly standing: Standing;
  readonly since: string;
  readonly note: string | null;
  readonly pathway: string | null;
  readonly stepsDone: number;
  readonly stepsTotal: number;
}

/** Everyone with a standing, newest first; a church has hundreds at most, so the list is read whole. */
export function useMembershipList(standing: Standing | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["membership-list", tenantId, standing],
    enabled: tenantId !== null,
    queryFn: async (): Promise<MembershipRow[]> => {
      const { data, error } = await supabase.rpc("membership_list", {
        p_tenant_id: tenantId ?? "",
        ...(standing === null ? {} : { p_standing: standing }),
      });
      if (error) throw error;
      return data.map((r) => ({
        personId: r.person_id,
        firstName: r.first_name,
        lastName: r.last_name,
        standing: r.standing,
        since: r.since,
        note: r.note,
        pathway: r.pathway,
        stepsDone: r.steps_done,
        stepsTotal: r.steps_total,
      }));
    },
  });
}

const pathwayViewSchema = z.object({
  standing: z.enum(["visitor", "attending", "in_process", "member", "inactive"]).nullable(),
  since: z.string().nullable(),
  note: z.string().nullable(),
  pathway: z
    .object({ id: z.string(), name: z.string(), description: z.string().nullable() })
    .nullable(),
  steps: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string().nullable(),
      completedOn: z.string().nullable(),
      note: z.string().nullable(),
    }),
  ),
});
export type PathwayView = z.infer<typeof pathwayViewSchema>;

/** Where a person is: standing, pathway, steps with completions (person_pathway). */
export function usePersonPathway(personId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["person-pathway", tenantId, personId],
    enabled: tenantId !== null && personId !== null,
    retry: false,
    queryFn: async (): Promise<PathwayView> => {
      const { data, error } = await supabase.rpc("person_pathway", {
        p_tenant_id: tenantId ?? "",
        p_person_id: personId ?? "",
      });
      if (error) throw error;
      return pathwayViewSchema.parse(data);
    },
  });
}

function useMembershipInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (personId?: string) => {
    await client.invalidateQueries({ queryKey: ["membership-list", tenantId] });
    await client.invalidateQueries({ queryKey: ["membership-pathways", tenantId] });
    await client.invalidateQueries({ queryKey: ["membership-steps", tenantId] });
    await client.invalidateQueries({ queryKey: ["person-pathway", tenantId] });
    if (personId !== undefined)
      await client.invalidateQueries({ queryKey: ["person", tenantId, personId] });
  };
}

export function useSavePathway() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useMembershipInvalidation();
  return useMutation({
    mutationFn: async (input: {
      pathwayId: string | null;
      name: string;
      description: string;
      sortOrder: number;
    }) => {
      const { data, error } = await supabase.rpc("set_membership_pathway", {
        p_tenant_id: tenantId ?? "",
        p_name: input.name,
        ...(input.pathwayId === null ? {} : { p_pathway_id: input.pathwayId }),
        ...(input.description.trim() === "" ? {} : { p_description: input.description }),
        p_sort_order: input.sortOrder,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(),
  });
}

export function useSaveStep() {
  const supabase = useSupabase();
  const invalidate = useMembershipInvalidation();
  return useMutation({
    mutationFn: async (input: {
      pathwayId: string;
      stepId: string | null;
      name: string;
      description: string;
      sortOrder: number;
    }) => {
      const { error } = await supabase.rpc("set_membership_step", {
        p_pathway_id: input.pathwayId,
        p_name: input.name,
        ...(input.stepId === null ? {} : { p_step_id: input.stepId }),
        ...(input.description.trim() === "" ? {} : { p_description: input.description }),
        p_sort_order: input.sortOrder,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useDeleteStep() {
  const supabase = useSupabase();
  const invalidate = useMembershipInvalidation();
  return useMutation({
    mutationFn: async (stepId: string) => {
      const { error } = await supabase.rpc("delete_membership_step", { p_step_id: stepId });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useSetStanding() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useMembershipInvalidation();
  return useMutation({
    mutationFn: async (input: {
      personId: string;
      standing: Standing;
      pathwayId: string | null;
      since: string;
      note: string;
    }) => {
      const { error } = await supabase.rpc("set_membership_standing", {
        p_tenant_id: tenantId ?? "",
        p_person_id: input.personId,
        p_standing: input.standing,
        ...(input.pathwayId === null ? {} : { p_pathway_id: input.pathwayId }),
        ...(input.since === "" ? {} : { p_since: input.since }),
        ...(input.note.trim() === "" ? {} : { p_note: input.note }),
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.personId),
  });
}

export function useCompleteStep() {
  const supabase = useSupabase();
  const invalidate = useMembershipInvalidation();
  return useMutation({
    mutationFn: async (input: { personId: string; stepId: string; done: boolean }) => {
      const { error } = input.done
        ? await supabase.rpc("complete_membership_step", {
            p_person_id: input.personId,
            p_step_id: input.stepId,
          })
        : await supabase.rpc("uncomplete_membership_step", {
            p_person_id: input.personId,
            p_step_id: input.stepId,
          });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.personId),
  });
}
